/**
 * Lightweight frontend error reporter.
 *
 * Captures:
 *   • React error boundary catches (manually called via reportError)
 *   • window.onerror (uncaught synchronous errors)
 *   • window.onunhandledrejection (uncaught Promise rejections)
 *
 * All errors are POSTed to /api/client-errors and persisted to system_logs.
 * No PII is sent — just message, stack, URL, and (if logged in) user_id.
 *
 * If the error itself is a network error reaching the API, we silently swallow
 * it instead of looping forever.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

let _inFlight = false;
let _lastReportTs = 0;
const RATE_LIMIT_MS = 2000;       // max one report every 2s
const DEDUPE_WINDOW_MS = 30_000;  // don't re-send same message within 30s
const _recentMessages = new Map();

function _shouldSend(message) {
  const now = Date.now();
  if (now - _lastReportTs < RATE_LIMIT_MS) return false;
  const last = _recentMessages.get(message);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  _recentMessages.set(message, now);
  // Trim the dedupe map to avoid unbounded growth
  if (_recentMessages.size > 50) {
    const cutoff = now - DEDUPE_WINDOW_MS;
    for (const [k, ts] of _recentMessages.entries()) {
      if (ts < cutoff) _recentMessages.delete(k);
    }
  }
  _lastReportTs = now;
  return true;
}

export async function reportError({ message, stack, componentStack, severity, extra }) {
  if (!message || !API_URL) return;
  const msg = String(message).slice(0, 2000);
  if (!_shouldSend(msg) || _inFlight) return;

  _inFlight = true;
  try {
    let userId = null;
    try {
      const cached = localStorage.getItem('hibff_user_cache');
      if (cached) userId = JSON.parse(cached)?.user_id || null;
    } catch (e) {
      // localStorage may be disabled (private browsing) — proceed without user_id.
      void e;
    }

    await fetch(`${API_URL}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: msg,
        stack: stack ? String(stack).slice(0, 8000) : null,
        component_stack: componentStack ? String(componentStack).slice(0, 8000) : null,
        url: window.location?.href?.slice(0, 500) || null,
        user_agent: navigator.userAgent?.slice(0, 500) || null,
        user_id: userId,
        severity: severity || 'ERROR',
        extra: extra || null,
      }),
      // Use keepalive so the request still goes through if the user navigates
      // away mid-error.
      keepalive: true,
    });
  } catch (e) {
    // The error reporter must NEVER throw — we'd loop forever.
    void e;
  } finally {
    _inFlight = false;
  }
}

/**
 * Wire global handlers for uncaught errors. Called once from src/index.js.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  if (window.__hibffErrorHandlersInstalled) return;
  window.__hibffErrorHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    reportError({
      message: event.message || 'Uncaught error',
      stack: event.error?.stack,
      severity: 'ERROR',
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      message: reason?.message || String(reason).slice(0, 2000) || 'Unhandled promise rejection',
      stack: reason?.stack,
      severity: 'ERROR',
      extra: { kind: 'unhandledrejection' },
    });
  });
}
